import {
  applyConnectedCharacterValues,
  withConnectedCharacterValues,
} from "./fieldConnections";
import { isGenericFormField } from "./genericFieldSupport";
import {
  isTextConnectedField,
  resolveConnectedTextSubmission,
} from "./textFieldConnections";
const mergeFieldValue = (
  field,
  formikValues = {},
  fieldNamesToOverlay = null,
) => {
  if (!field?.name) {
    return field;
  }

  if (fieldNamesToOverlay && !fieldNamesToOverlay.has(field.name)) {
    return field;
  }

  return {
    ...field,
    value: formikValues?.[field.name] ?? field?.value ?? "",
  };
};

const mergeFields = (
  fields = [],
  formikValues = {},
  fieldNamesToOverlay = null,
) => {
  if (!Array.isArray(fields)) {
    return [];
  }

  return fields.map((field) => ({
    ...mergeFieldValue(field, formikValues, fieldNamesToOverlay),
    options: Array.isArray(field?.options)
      ? field.options.map((option) => ({ ...option }))
      : field?.options,
  }));
};

const normalizeCollection = (collection) => {
  if (Array.isArray(collection)) return collection;

  if (collection && typeof collection === "object") {
    return Object.values(collection).filter(
      (item) => item && typeof item === "object" && !Array.isArray(item),
    );
  }

  return [];
};

const mergeSavedFieldValues = (fields, valuesContainer) => {
  if (!valuesContainer || typeof valuesContainer !== "object") return fields;

  return fields.map((field) => {
    if (
      field?.name &&
      Object.prototype.hasOwnProperty.call(valuesContainer, field.name)
    ) {
      const savedValue = valuesContainer[field.name];
      const hasPopulatedSavedValue =
        savedValue !== null && savedValue !== undefined && savedValue !== "";
      const hasPopulatedWidgetValue =
        field?.value !== null &&
        field?.value !== undefined &&
        field?.value !== "";

      // Legacy scraped NOCs keep the original blank form values beside a
      // populated nested `fields` array. Those stale blanks must not erase the
      // values returned by the property scraper. Explicit edits are applied
      // later by buildResolvedDocumentPayload, so populated outer values still
      // remain authoritative here.
      if (hasPopulatedSavedValue || !hasPopulatedWidgetValue) {
        return { ...field, value: savedValue ?? "" };
      }
    }

    return field;
  });
};

export const normalizeFieldsToMap = (fields) => {
  if (Array.isArray(fields)) {
    return fields.reduce((acc, field) => {
      if (field?.name) {
        acc[field.name] = field?.value ?? "";
      }

      return acc;
    }, {});
  }

  if (fields && typeof fields === "object") {
    return fields;
  }

  return {};
};

export const getDocumentByFormId = (documents = {}, formId = null) => {
  if (!formId || !documents || typeof documents !== "object") {
    return null;
  }

  return documents?.[formId] || null;
};

export const getDocumentFields = (documentPayload) => {
  if (!documentPayload || typeof documentPayload !== "object") {
    return [];
  }

  const valuesContainer = documentPayload?.fields;
  const sections = normalizeCollection(
    documentPayload?.sections || valuesContainer?.sections,
  );

  if (sections.length > 0) {
    const fields = sections.flatMap((section) =>
      normalizeCollection(section?.fields),
    );
    return mergeSavedFieldValues(fields, valuesContainer);
  }

  const definitions =
    valuesContainer && !Array.isArray(valuesContainer)
      ? normalizeCollection(valuesContainer?.fields)
      : normalizeCollection(valuesContainer);

  return mergeSavedFieldValues(definitions, valuesContainer);
};

export const getDocumentSections = (documentPayload) => {
  if (!documentPayload || typeof documentPayload !== "object") {
    return [];
  }

  const valuesContainer = documentPayload?.fields;
  const sections = normalizeCollection(
    documentPayload?.sections || valuesContainer?.sections,
  );

  return sections.map((section) => ({
    ...section,
    fields: mergeSavedFieldValues(
      normalizeCollection(section?.fields),
      valuesContainer,
    ),
  }));
};

export const getDocumentFieldInstances = (documentPayload) => {
  if (!documentPayload || typeof documentPayload !== "object") {
    return [];
  }

  return normalizeCollection(documentPayload?.field_instances);
};

export const getDocumentFieldValueByName = (
  documentPayload,
  fieldName,
  fallback = "",
) => {
  if (!fieldName) {
    return fallback;
  }

  const match = getDocumentFields(documentPayload).find(
    (field) => field?.name === fieldName,
  );

  return match?.value ?? fallback;
};

export const getDocumentFieldValueByNames = (
  documentPayload,
  fieldNames = [],
  fallback = "",
) => {
  if (!Array.isArray(fieldNames) || fieldNames.length === 0) {
    return fallback;
  }

  for (const fieldName of fieldNames) {
    const value = getDocumentFieldValueByName(
      documentPayload,
      fieldName,
      undefined,
    );
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return fallback;
};

export const getDocumentFieldValueByFieldId = (
  documentPayload,
  fieldId,
  fallback = "",
) => {
  if (!fieldId) {
    return fallback;
  }

  const match = getDocumentFields(documentPayload).find(
    (field) => field?.field_id === fieldId,
  );

  return match?.value ?? fallback;
};

export const getAllDocumentFields = (documents = {}, excludeFormId = null) => {
  if (!documents || typeof documents !== "object") {
    return [];
  }

  return Object.entries(documents).flatMap(([formId, documentPayload]) => {
    if (excludeFormId && formId === excludeFormId) {
      return [];
    }

    return getDocumentFields(documentPayload).map((field) => ({
      ...field,
      __formId: formId,
    }));
  });
};

export const getSharedFieldValueFromDocuments = ({
  documents = {},
  fieldId = null,
  fieldName = null,
  excludeFormId = null,
} = {}) => {
  const allFields = getAllDocumentFields(documents, excludeFormId).filter(
    (field) => !isGenericFormField(field),
  );

  const match = allFields.find((field) => {
    if (fieldId && field?.field_id === fieldId) {
      return (
        field?.value !== undefined &&
        field?.value !== null &&
        field?.value !== ""
      );
    }

    if (fieldName && field?.name === fieldName) {
      return (
        field?.value !== undefined &&
        field?.value !== null &&
        field?.value !== ""
      );
    }

    return false;
  });

  return match?.value ?? "";
};

const clearInactiveSignatureFieldValues = (documentPayload) => {
  const signatureTypeId = "9a1bb637-07af-4043-a99a-59616c6db63c";

  const signatureOptionIds = new Set([
    "73d5a2dd-72e3-4d2d-a01d-e12dab18981f", // Individual
    "82eefc31-9e8a-425d-9e5d-cb850f23c875", // Corporation
  ]);

  const hasSections =
    Array.isArray(documentPayload.sections) &&
    documentPayload.sections.length > 0;

  const fields = hasSections
    ? documentPayload.sections.flatMap((section) => section.fields)
    : documentPayload.fields;

  const signatureFields = fields.filter(
    (field) => field?.field_id === signatureTypeId,
  );

  const resolveOptionId = (value, group) => {
    const raw = value && typeof value === "object" ? value.value : value;

    if (signatureOptionIds.has(raw)) return raw;

    const option = group.options?.find(
      (item) => item.name === raw || item.value === raw,
    );

    return signatureOptionIds.has(option?.field_id) ? option.field_id : null;
  };

  const clearField = (field) => {
    const parents = Array.isArray(field.parents) ? field.parents : [];

    const isInactive = parents.some((parent) => {
      const group = signatureFields.find((candidate) => {
        if (parent.field_instance_id) {
          return candidate.field_instance_id === parent.field_instance_id;
        }

        return Boolean(parent.name) && candidate.name === parent.name;
      });

      if (!group) return false;

      const selectedValue = Array.isArray(group.value)
        ? group.value[0]
        : group.value;

      const selectedId = resolveOptionId(selectedValue, group);

      const allowedValues = Array.isArray(parent.value)
        ? parent.value
        : [parent.value];

      const allowedIds = allowedValues.map((value) =>
        resolveOptionId(value, group),
      );

      // Only evaluate recognized Signature Type conditions.
      if (!selectedId || !allowedIds.length || allowedIds.some((id) => !id)) {
        return false;
      }

      return !allowedIds.includes(selectedId);
    });

    return isInactive ? { ...field, value: "" } : field;
  };

  return hasSections
    ? {
        ...documentPayload,
        sections: documentPayload.sections.map((section) => ({
          ...section,
          fields: section.fields.map(clearField),
        })),
      }
    : {
        ...documentPayload,
        fields: documentPayload.fields.map(clearField),
      };
};

export const buildResolvedDocumentPayload = (
  selectedDocument,
  documents = {},
  formikValues = {},
  fieldNamesToOverlay = null,
  { clearInactiveSignatureFields = false } = {},
) => {
  if (!selectedDocument?.form_id) {
    return null;
  }

  const currentDocument = getDocumentByFormId(
    documents,
    selectedDocument.form_id,
  );

  if (!currentDocument) {
    return null;
  }

  const sections = normalizeCollection(
    currentDocument?.sections || currentDocument?.fields?.sections,
  );

  const connectionFields = getDocumentFields(currentDocument);

  // Resolve text flow and reject overflow before sending the document.
  const resolvedTextFields = resolveConnectedTextSubmission(
    connectionFields,
    formikValues,
    fieldNamesToOverlay,
  );

  const getWidgetKey = (field) =>
    field?.field_instance_id
      ? `instance:${field.field_instance_id}`
      : field?.name
        ? `name:${field.name}`
        : null;

  const resolvedTextByKey = new Map(
    resolvedTextFields
      .filter((field) => getWidgetKey(field))
      .map((field) => [getWidgetKey(field), field]),
  );

  // Preserve existing character connection handling.
  const resolvedConnectionFields = applyConnectedCharacterValues(
    connectionFields,
    formikValues,
  );

  const resolvedFormikValues = withConnectedCharacterValues(
    connectionFields,
    formikValues,
  );

  const resolvedByName = new Map(
    resolvedConnectionFields.map((field) => [field.name, field]),
  );

  const mergeWithConnections = (fields) =>
    mergeFields(fields, resolvedFormikValues, fieldNamesToOverlay).map(
      (field) => {
        const resolvedText = resolvedTextByKey.get(getWidgetKey(field));

        if (resolvedText) {
          return {
            ...field,
            value: resolvedText.value,
            ...(isTextConnectedField(resolvedText)
              ? { text_connection: resolvedText.text_connection }
              : {}),
          };
        }

        const resolvedCharacter = resolvedByName.get(field.name);

        return resolvedCharacter?.connection?.mode === "character"
          ? {
              ...field,
              value: resolvedCharacter.value,
              connection: resolvedCharacter.connection,
            }
          : field;
      },
    );

  const mergedDocument =
    sections.length > 0
      ? {
          ...currentDocument,
          sections: sections.map((section) => ({
            ...section,
            fields: mergeWithConnections(section.fields),
          })),
        }
      : {
          ...currentDocument,
          fields: mergeWithConnections(connectionFields),
        };

  return clearInactiveSignatureFields
    ? clearInactiveSignatureFieldValues(mergedDocument)
    : mergedDocument;
};

const SPECIAL_RENDERER_FIELD_IDS = new Set(["pais", "pass", "generic_table"]);

const isSpecialRendererField = (field) =>
  SPECIAL_RENDERER_FIELD_IDS.has(
    String(field?.field_id ?? "")
      .trim()
      .toLowerCase(),
  );

export const removeSpecialRendererFields = (documentPayload) => {
  if (!documentPayload || typeof documentPayload !== "object") {
    return documentPayload;
  }

  if (Array.isArray(documentPayload?.sections)) {
    return {
      ...documentPayload,
      sections: documentPayload.sections.map((section) => ({
        ...section,
        fields: Array.isArray(section?.fields)
          ? section.fields.filter((field) => !isSpecialRendererField(field))
          : section?.fields,
      })),
    };
  }

  return {
    ...documentPayload,
    fields: Array.isArray(documentPayload?.fields)
      ? documentPayload.fields.filter((field) => !isSpecialRendererField(field))
      : documentPayload?.fields,
  };
};

export const extractFlatValuesFromDocumentPayload = (documentPayload) => {
  if (!documentPayload || typeof documentPayload !== "object") {
    return {};
  }

  const fields = getDocumentFields(documentPayload);

  return fields.reduce((acc, field) => {
    if (!field?.name) {
      return acc;
    }

    acc[field.name] = field?.value ?? "";
    return acc;
  }, {});
};
